import { SummaryReport } from 'schemas/SummaryReport.schema.ts';
import { IterationDetails } from 'schemas/CampaignConfig.schema.ts';
import { UserData } from 'hooks/useUser.ts';
import { WarningCallout } from 'nhsuk-react-components';
import { ITERATION_STATUS } from 'schemas/CampaignApiDetails.schema.ts';

interface ISummaryReportWarningProps {
  report: SummaryReport;
  currentIterationDetails: IterationDetails | undefined;
  isCurrentSummaryReport: boolean;
  user: UserData;
  teamId: string;
}
export default function SummaryReportWarning({
  report,
  currentIterationDetails,
  isCurrentSummaryReport,
  user,
  teamId,
}: ISummaryReportWarningProps) {
  if (!isCurrentSummaryReport) {
    return (
      <WarningCallout>
        <WarningCallout.Heading>
          This report has been archived
        </WarningCallout.Heading>
        <p>
          You cannot approve this report because it has been archived.
          <br />
          Reports must be approved and sent to NHS Notify by 5pm the day after
          they are generated.
        </p>
        <p>
          A newer version of this report may be available. Check the summary
          reports page.
        </p>
        <p>Return to the summary reports page to continue.</p>
      </WarningCallout>
    );
  }

  if (report.dry_run) {
    return (
      <WarningCallout>
        <WarningCallout.Heading>
          This is a dry run summary report
        </WarningCallout.Heading>
        <p>
          It shows what would happen in a live run and lets you check the number
          of people who would be sent communications. You cannot approve or
          reject this report. To change the numbers, revert the iteration to
          draft and update it as needed.
        </p>
      </WarningCallout>
    );
  }

  if (report.summary_report_totals.totals.invited === 0) {
    return (
      <WarningCallout>
        <WarningCallout.Heading>
          This report invites 0 people
        </WarningCallout.Heading>
        <p>
          This report cannot be approved or sent to NHS Notify because it
          invites 0 people. To make changes, you need to revert the iteration to
          draft first at the bottom of the page.
        </p>
      </WarningCallout>
    );
  }

  if (
    currentIterationDetails?.status === ITERATION_STATUS.REPORT_IN_REVIEW
    && !report.reviewers.includes(user.userId)
    && user.roles.includes(`${teamId}:approve`)
    && isCurrentSummaryReport
  ) {
    return (
      <WarningCallout>
        <WarningCallout.Heading>
          You’re not a reviewer for this summary report
        </WarningCallout.Heading>
        <p>
          You can still view it, but you do not have permission to approve or
          reject.
        </p>
      </WarningCallout>
    );
  }

  return null;
}
